
import React, { useState, useEffect } from 'react';
import { Container } from '@/components/ui/container';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield, UserX, Heart, ShoppingCart, Tags, Archive, Home } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import UserListings from '@/components/user/UserListings';
import ProfileInfo from '@/components/profile/ProfileInfo';
import AdminTools from '@/components/profile/AdminTools';
import AuthSection from '@/components/auth/AuthSection';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
// UserPurchases and UserSales are no longer directly used here for dialogs
import SubscriptionInfo from '@/components/profile/SubscriptionInfo';
import RedeemVoucher from '@/components/profile/RedeemVoucher';
import PremiumVouchers from '@/components/profile/PremiumVouchers';
import { useToast } from '@/hooks/use-toast';
import { deleteUser } from '@/lib/actions/userActions';
import { getUserListings, getArchivedListings } from '@/lib/actions';
import { useQuery } from '@tanstack/react-query';
import { useOrderAutoComplete } from '@/hooks/useOrderAutoComplete';

const Profile = () => {
  const { profile, isAuthenticated, logout } = useAuth();
  const isAdmin = profile?.role === 'admin';
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isDeactivationDialogOpen, setIsDeactivationDialogOpen] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  // Removed state for purchasesOpen and salesOpen

  // Use the order auto-complete hook to automatically mark orders as completed after 30 days
  useOrderAutoComplete();

  // Fetch user listings data for counters
  const {
    data: userListings = []
  } = useQuery({
    queryKey: ['userListings', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      return await getUserListings(profile.id);
    },
    enabled: !!profile?.id,
  });

  // Fetch archived listings for counter
  const {
    data: archiveListings = []
  } = useQuery({
    queryKey: ['archivedListings', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      return await getArchivedListings(profile.id);
    },
    enabled: !!profile?.id,
  });

  // Calculate counters for UI
  const activeListingsCount = userListings.filter(listing => listing.is_active).length;
  const inactiveListingsCount = userListings.filter(listing => !listing.is_active).length;
  const archivedListingsCount = archiveListings.length;

  // Handle go back
  const handleGoBack = () => {
    navigate(-1);
  };

  // Handle user account deactivation
  const handleDeactivateAccount = async () => {
    if (!profile?.id) return;

    setIsDeactivating(true);
    try {
      const success = await deleteUser(profile.id);

      if (success) {
        toast({
          title: "Llogaria u çaktivizua",
          description: "Llogaria juaj u çaktivizua me sukses.",
          variant: "default"
        });

        // Logout and redirect to home
        await logout();
        navigate('/');
      } else {
        throw new Error("Gabim gjatë çaktivizimit të llogarisë");
      }
    } catch (error) {
      toast({
        title: "Gabim",
        description: "Ndodhi një gabim gjatë çaktivizimit të llogarisë.",
        variant: "destructive"
      });
    } finally {
      setIsDeactivating(false);
      setIsDeactivationDialogOpen(false);
    }
  };

  return (
    <Container>
      <div className="py-10">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={handleGoBack} className="rounded-full">
              <ArrowLeft size={16} />
            </Button>
            <h1 className="text-3xl font-bold">Profili Juaj</h1>
          </div>
          {isAuthenticated && (
            <div className="flex items-center gap-2">
              <Link to="/">
                <Button className="flex items-center gap-2 bg-green-600 hover:bg-green-700 mr-2">
                  <Home size={16} />
                  Faqja Kryesore
                </Button>
              </Link>
              {isAdmin && (
                <Link to="/admin-dashboard">
                  <Button className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700">
                    <Shield size={16} />
                    Admin Dashboard
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>

        {isAuthenticated ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <ProfileInfo />

              <div className="mt-8">
                <SubscriptionInfo />
              </div>

              <div className="mt-8">
                <RedeemVoucher userId={profile?.id || ''} />
              </div>

              {/* Add Premium Vouchers component */}
              <div className="mt-8">
                <PremiumVouchers userId={profile?.id || ''} />
              </div>

              <div className="mt-8">
                <div className="mb-4 flex flex-wrap items-center gap-4">
                  <div className="flex items-center text-gray-700 dark:text-gray-300">
                    <ShoppingCart size={16} className="mr-1 text-blue-500" />
                    <span className="text-sm">{activeListingsCount} shpallje aktive</span>
                  </div>
                  <div className="flex items-center text-gray-700 dark:text-gray-300">
                    <Archive size={16} className="mr-1 text-orange-500" />
                    <span className="text-sm">{inactiveListingsCount} shpallje jo aktive</span>
                  </div>
                  <div className="flex items-center text-gray-700 dark:text-gray-300">
                    <Archive size={16} className="mr-1 text-gray-500" />
                    <span className="text-sm">{archivedListingsCount} shpallje të arkivuara</span>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/purchases">
                  <Button
                    className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                    data-purchases-button="true"
                  >
                    <ShoppingCart size={16} />
                    Blerjet e Mia
                  </Button>
                </Link>
                <Link to="/sales">
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
                    data-sales-button="true"
                  >
                    <Tags size={16} />
                    Shitjet e Mia
                  </Button>
                </Link>
                <Button
                  variant="destructive"
                  onClick={() => setIsDeactivationDialogOpen(true)}
                  className="flex items-center gap-2"
                >
                  <UserX size={16} />
                  Çaktivizo Llogarinë
                </Button>
              </div>

              {profile && (
                <div className="mt-8">
                  <UserListings userId={profile.id} />
                </div>
              )}
            </div>

            <div className="space-y-6">
              {isAdmin && <AdminTools />}
            </div>
          </div>
        ) : (
          <AuthSection />
        )}

        {/* Removed Purchases Dialog */}
        {/* Removed Sales Dialog */}

        {/* Deactivation Confirmation Dialog */}
        <Dialog
          open={isDeactivationDialogOpen}
          onOpenChange={setIsDeactivationDialogOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Çaktivizo Llogarinë</DialogTitle>
              <DialogDescription>
                A jeni të sigurt që dëshironi të çaktivizoni llogarinë tuaj? Ky veprim nuk mund të kthehet pas.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsDeactivationDialogOpen(false)}
              >
                Anulo
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeactivateAccount}
                disabled={isDeactivating}
              >
                {isDeactivating ? "Duke çaktivizuar..." : "Çaktivizo Llogarinë"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Container>
  );
};

export default Profile;
